import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import Lottie from 'lottie-react';
import { text } from 'style/mixin';
import fonts from 'style/fonts';
import { mq } from 'style/variables';
import bgApply from 'assets/images/career/bg-apply.png';
import ArrowSvg from 'assets/images/career/arrow.svg';
import lottieRunning from 'assets/motions/career/running/data.json';
import lottiePopup from 'assets/motions/career/popup/data.json';
import { useInView } from 'react-intersection-observer';

// 定数定義
const ANIMATION_CONSTANTS = {
  TRANSITION_DURATION: '0.3s',
} as const;

const ApplyWrapper = styled.div`
  position: relative;
  height: 455px;
  overflow: hidden;

  ${mq.onlysp} {
    height: 416px;
  }

  .lottie-running {
    position: absolute;
    bottom: 0;
    right: 0;

    svg {
      width: inherit !important;
      height: calc(100 / 1440 * 100vw) !important;
      max-height: calc(100px / 1440 * 2310) !important;
      min-height: 100px !important;
    }

    ${mq.onlysp} {
      width: auto;
      height: 100px;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }
  }

  .lottie-popup {
    position: relative;
    width: 100vw;
    height: 100%;
    opacity: 0;
    visibility: hidden;
    transition: opacity ${ANIMATION_CONSTANTS.TRANSITION_DURATION} ease, 
                visibility ${ANIMATION_CONSTANTS.TRANSITION_DURATION} ease;

    &.show {
      opacity: 1;
      visibility: visible;
    }

    svg {
      position: absolute;
      bottom: 0;
      right: 0;
      width: auto !important;
      height: calc(455 / 1440 * 100vw) !important;
      max-height: calc(455px / 1440 * 2310) !important;
      min-height: 455px !important;

      ${mq.onlysp} {
        height: 100% !important;
      }
    }
  }
`;

const BackgroundApply = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${bgApply});
  background-size: cover;
  background-position: right bottom;
  background-repeat: no-repeat;

  ${mq.onlysp} {
    background-position: center bottom;
  }
`;

const ApplyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 35px;
  position: absolute;
  top: 82px;
  left: 140px;
  max-width: 100%;
  color: #fff;
  z-index: 1;

  ${mq.onlysp} {
    top: 60px;
    left: 0;
    transform: none;
    width: calc(100% - 64px);
    margin: 0 32px;
    gap: 44px;
  }
`;

const ApplyTitle = styled.div`
  ${text(72, 0.75, true)}
  font-family: ${fonts.enGothic};
  color: #fff;
  font-weight: 600;
  letter-spacing: -0.06em;

  ${mq.onlysp} {
    ${text(50, 0.75, true)}
  }
`;

const SpBr = styled.br`
  display: none;
  ${mq.onlysp} {
    display: inline;
  }
`;

// ボタン関連の定数
const BUTTON_CONSTANTS = {
  WIDTH: 275,
  HEIGHT: 48,
  ARROW_WIDTH: 30,
  ARROW_HEIGHT: 8,
  ARROW_RIGHT: 20,
  TRANSITION_DURATION: '0.5s',
  HOVER_DELAY: '0.1s',
  ARROW_DELAY: '0.2s',
} as const;

const ApplyBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  ${text(14, 1.4, true)}
  font-family: ${fonts.jaGothic};
  color: #fff;
  font-weight: 1000;
  width: ${BUTTON_CONSTANTS.WIDTH}px;
  height: ${BUTTON_CONSTANTS.HEIGHT}px;
  border: 1px solid #fff;
  position: relative;
  text-decoration: none;
  background-color: transparent;
  z-index: 2;
  overflow: hidden;
  transition: color ${BUTTON_CONSTANTS.TRANSITION_DURATION} ease ${BUTTON_CONSTANTS.HOVER_DELAY};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background-color: #fff;
    transition: left ${BUTTON_CONSTANTS.TRANSITION_DURATION} ease;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: ${BUTTON_CONSTANTS.ARROW_RIGHT}px;
    transform: translateY(-50%);
    width: ${BUTTON_CONSTANTS.ARROW_WIDTH}px;
    height: ${BUTTON_CONSTANTS.ARROW_HEIGHT}px;
    display: block;
    background-image: url(${ArrowSvg});
    background-repeat: no-repeat;
    background-position: center;
    filter: brightness(0) invert(1);
    transition: filter ${BUTTON_CONSTANTS.TRANSITION_DURATION} ease ${BUTTON_CONSTANTS.ARROW_DELAY};
  }

  &:hover {
    color: #000;
    
    &::before {
      left: 0;
    }
    
    &::after {
      filter: brightness(0) invert(0);
      transition: filter ${BUTTON_CONSTANTS.TRANSITION_DURATION} ease;
    }
  }
`;

// アニメーション関連の定数
const ANIMATION_FRAMES = {
  MOBILE_RUNNING_END: 44,
  DESKTOP_RUNNING_END: 66,
  POPUP_PATTERN_FRAMES: 120,
  POPUP_APPEAR_FRAMES: 30,
  POPUP_DISAPPEAR_START_OFFSET: 60,
  POPUP_DISAPPEAR_FRAMES: 30,
  TOTAL_PATTERNS: 6,
} as const;

const BREAKPOINTS = {
  MOBILE: 768,
} as const;

type CareerDetailApplyProps = {
  applyUrl?: string;
};

export const CareerDetailApply = ({ applyUrl }: CareerDetailApplyProps) => {
  const { ref: wrapperRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const [selectedAnimation, setSelectedAnimation] = useState(0);
  const [isRunningComplete, setIsRunningComplete] = useState(false);
  const runningRef = useRef<any>(null);
  const popupRef = useRef<any>(null);

  // ユーティリティ関数
  const isMobile = () => window.innerWidth <= BREAKPOINTS.MOBILE;

  // アニメーション制御関数
  const playStartRunning = () => {
    if (!runningRef.current) return;
    
    const endFrame = isMobile() 
      ? ANIMATION_FRAMES.MOBILE_RUNNING_END 
      : ANIMATION_FRAMES.DESKTOP_RUNNING_END;
    
    runningRef.current.playSegments([0, endFrame], true);
  };

  const playEndRunning = () => {
    if (!runningRef.current) return;
    runningRef.current.stop();
  };

  const playStartPopup = () => {
    if (!popupRef.current) return;
    
    // ランダムなアニメーションパターンを選択
    const randomAnimation = Math.floor(Math.random() * ANIMATION_FRAMES.TOTAL_PATTERNS);
    setSelectedAnimation(randomAnimation);

    // 出現アニメーションを再生
    const startFrame = randomAnimation * ANIMATION_FRAMES.POPUP_PATTERN_FRAMES;
    const endFrame = startFrame + ANIMATION_FRAMES.POPUP_APPEAR_FRAMES;
    
    popupRef.current.playSegments([startFrame, endFrame], true);
  };

  const playEndPopup = () => {
    if (!popupRef.current) return;
    
    // 消去アニメーションを再生
    const startFrame = selectedAnimation * ANIMATION_FRAMES.POPUP_PATTERN_FRAMES + 
                      ANIMATION_FRAMES.POPUP_DISAPPEAR_START_OFFSET;
    const endFrame = startFrame + ANIMATION_FRAMES.POPUP_DISAPPEAR_FRAMES;
    
    popupRef.current.playSegments([startFrame, endFrame], true);
  };

  // イベントハンドラー
  const handleRunningDOMLoaded = () => {
    playStartRunning();
  };

  const handleRunningComplete = () => {
    setIsRunningComplete(true);

    // モバイルの場合は即座にポップアップアニメーションを実行
    if (isMobile()) {
      playEndRunning();
      playStartPopup();
    }
  };

  const handleMouseEnter = () => {
    // デスクトップでランニングアニメーション完了後のみホバーアニメーションを実行
    if (isRunningComplete && !isMobile()) {
      playStartPopup();
    }
  };

  const handleMouseLeave = () => {
    // デスクトップでランニングアニメーション完了後のみホバー解除アニメーションを実行
    if (isRunningComplete && !isMobile()) {
      playEndPopup();
    }
  };

  return (
    <ApplyWrapper ref={wrapperRef}>
      <BackgroundApply />
      <ApplyContainer>
        <ApplyTitle>
          WHY <SpBr />
          DON&apos;T YOU
          <br />
          JOIN US?
        </ApplyTitle>
        <ApplyBtn
          href={applyUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleMouseEnter}
          onTouchEnd={handleMouseLeave}
        >
          応募する
        </ApplyBtn>
      </ApplyContainer>
      {inView && (
        <>
          <Lottie
            className={'lottie-running'}
            animationData={lottieRunning}
            loop={false}
            autoplay={false}
            onDOMLoaded={handleRunningDOMLoaded}
            onComplete={handleRunningComplete}
            lottieRef={runningRef}
          />

          <Lottie
            className={'lottie-popup show'}
            animationData={lottiePopup}
            loop={false}
            autoplay={false}
            lottieRef={popupRef}
          />
        </>
      )}
    </ApplyWrapper>
  );
};
